import React, { useState, useEffect } from 'react'
import useSWR from 'swr'
import SpektrixFetcher from '@lib/spektrix-fetcher'
import {
  removeTicketsFromBasket,
  addTicketsToBasket,
  updateGiftVoucherInBasket,
  removeGiftVoucherFromBasket,
  updateMembershipInBasket,
  removeMembershipFromBasket
} from '@lib/spektrix'
import { syncMealDealsWithTickets } from '@lib/meal-deal-sync'
import { format, parseISO } from 'date-fns'
import { standardWithTime } from '@lib/date-time-formats'
import BasketUpdateEvent from '@lib/basket-update-event'
import PromoCodeSection from './promo-code-section'
import {
  ArrowPathIcon,
  ShoppingBagIcon,
  XMarkIcon,
  ArrowLeftIcon,
  CakeIcon,
  TicketIcon,
  TrashIcon,
  PlusCircleIcon,
  MinusCircleIcon,
  GiftIcon,
  PencilIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline'

const BASKET_DELAY = 1500 // Match the delay from add_to_basket page

const BasketView = () => {
  const [eventDetails, setEventDetails] = useState({})
  const [instanceDetails, setInstanceDetails] = useState({})
  const [relatedEvents, setRelatedEvents] = useState({})
  const [removingTickets, setRemovingTickets] = useState({})
  const [updatingBasket, setUpdatingBasket] = useState(false)
  const [editingGiftVoucher, setEditingGiftVoucher] = useState(null)
  const [removingGiftVouchers, setRemovingGiftVouchers] = useState({})
  const [editingMembership, setEditingMembership] = useState(null)
  const [removingMemberships, setRemovingMemberships] = useState({})

  // First fetch the basket ID from Spektrix
  const { data: spektrixBasket } = useSWR(
    '/basket?$expand=3',
    SpektrixFetcher,
    {
      revalidateOnFocus: true,
      shouldRetryOnError: false
    }
  )

  // Check if basket has items
  const hasItems =
    spektrixBasket &&
    (spektrixBasket.tickets?.length > 0 ||
      spektrixBasket.items?.length > 0 ||
      spektrixBasket.donations?.length > 0 ||
      spektrixBasket.giftVouchers?.length > 0 ||
      spektrixBasket.membershipSubscriptions?.length > 0)

  // Only fetch augmented basket data if there are items
  const {
    data: basketData,
    error,
    mutate
  } = useSWR(
    spektrixBasket?.id && hasItems ? `/baskets/${spektrixBasket.id}` : null,
    async url => {
      const response = await fetch(url, {
        headers: { Accept: 'application/json' }
      })

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ error: 'Unknown error' }))
        throw new Error(
          errorData.error || `HTTP ${response.status}: ${response.statusText}`
        )
      }

      return response.json()
    },
    {
      revalidateOnFocus: true,
      shouldRetryOnError: false
    }
  )

  const isLoading = !spektrixBasket && !error

  // Listen for basket updates
  useEffect(() => {
    const handleBasketUpdate = () => {
      mutate()
    }

    window.addEventListener(BasketUpdateEvent, handleBasketUpdate)
    return () =>
      window.removeEventListener(BasketUpdateEvent, handleBasketUpdate)
  }, [mutate])

  // Extract event and instance details from augmented basket data
  useEffect(() => {
    if (basketData && basketData.tickets) {
      const events = {}
      const instances = {}

      basketData.tickets.forEach(ticket => {
        if (ticket.localEventData) {
          // Key by local event ID for consistency with related events
          const localEventId = ticket.localEventData.id
          if (localEventId) {
            events[localEventId] = ticket.localEventData
          }
          // Also keep spektrix ID mapping
          if (ticket.event) {
            events[ticket.event.id] = ticket.localEventData
          }
        }
        if (ticket.localInstanceData && ticket.instance) {
          instances[ticket.instance.id] = ticket.localInstanceData
        }
      })

      setEventDetails(events)
      setInstanceDetails(instances)
    }
  }, [basketData])

  // Update related events when event details change
  useEffect(() => {
    // Use related events from basketData if available, otherwise fall back to eventDetails
    if (
      basketData?.related_events &&
      typeof basketData.related_events === 'object'
    ) {
      // basketData.related_events is now a hash keyed by event ID
      setRelatedEvents(basketData.related_events)
    } else {
      // Fall back to related events from eventDetails
      const related = {}
      Object.entries(eventDetails).forEach(([eventId, event]) => {
        if (event?.related_events && event.related_events.length > 0) {
          related[eventId] = event.related_events
        }
      })
      setRelatedEvents(related)
    }
  }, [eventDetails, basketData])

  const handleRemoveTicket = async ticketId => {
    try {
      setRemovingTickets(prev => ({ ...prev, [ticketId]: true }))
      await removeTicketsFromBasket([ticketId])
      await mutate()
    } catch (err) {
      alert('Failed to remove ticket. Please try again.')
    } finally {
      setRemovingTickets(prev => ({ ...prev, [ticketId]: false }))
    }
  }

  const handleQuantityChange = async (group, delta) => {
    if (delta > 0) {
      // Add one more ticket of the same type
      try {
        setUpdatingBasket(true)

        // Get the first ticket to extract the necessary IDs
        const sampleTicket = group.tickets[0]

        // Extract the ticket type ID - NOT the band ID!
        const typeId =
          typeof sampleTicket.type === 'object'
            ? sampleTicket.type.id
            : sampleTicket.type

        // Try to get plan ID from group first, then from ticket
        let planId = group.planId || group.plan
        if (!planId && sampleTicket.plan) {
          planId =
            typeof sampleTicket.plan === 'object'
              ? sampleTicket.plan.id
              : sampleTicket.plan
        }
        if (!planId) {
          planId = sampleTicket.planId
        }

        // Create ticket data in the format expected by Spektrix API
        const ticketData = [
          {
            instance: group.instance.spektrix_v3_id || group.instance.id,
            seatingPlan: planId,
            type: typeId
          }
        ]

        // Find instance data for meal deal info
        const instanceId = group.instance.id
        const eventId = group.event.id
        const instanceData = groupedItems[eventId]?.instances?.[instanceId]

        // Look for meal deals in the basket that belong to this instance
        const eventMealDeals = instanceData?.mealDeals || []

        // Get meal deal instances from any ticket's localInstanceData
        let mealDealInstances = []
        if (group.tickets.length > 0 && group.tickets[0].localInstanceData) {
          // Get meal deal instances from the ticket's instance data
          const ticketInstanceData = group.tickets[0].localInstanceData
          if (ticketInstanceData.meal_deal_instances) {
            mealDealInstances = ticketInstanceData.meal_deal_instances
          }
        }

        // Use the common sync function
        await syncMealDealsWithTickets({
          action: 'add',
          ticketsToProcess: ticketData,
          instanceData: instanceData,
          mealDealInfo: {
            meal_deals: mealDealInstances
          },
          currentBasket: basketData
        })

        // Dispatch event and wait for basket update
        setTimeout(() => {
          window.dispatchEvent(new Event(BasketUpdateEvent))
          mutate()
          setUpdatingBasket(false)
        }, BASKET_DELAY)
      } catch (err) {
        alert('Failed to add ticket. Please try again.')
        setUpdatingBasket(false)
      }
    } else if (delta < 0 && group.tickets.length > 0) {
      try {
        setUpdatingBasket(true)

        // Remove one ticket (the last one)
        const ticketToRemove = group.tickets[group.tickets.length - 1]

        // Find instance data for meal deal info
        const instanceId = group.instance.id
        const eventId = group.event.id
        const instanceData = groupedItems[eventId]?.instances?.[instanceId]

        // Get meal deal instances from any ticket's localInstanceData
        let mealDealInstances = []
        if (group.tickets.length > 0 && group.tickets[0].localInstanceData) {
          // Get meal deal instances from the ticket's instance data
          const ticketInstanceData = group.tickets[0].localInstanceData
          if (ticketInstanceData.meal_deal_instances) {
            mealDealInstances = ticketInstanceData.meal_deal_instances
          }
        }

        // Use the common sync function
        await syncMealDealsWithTickets({
          action: 'remove',
          ticketsToProcess: [ticketToRemove.id],
          instanceData: instanceData,
          mealDealInfo: {
            meal_deals: mealDealInstances
          },
          currentBasket: basketData
        })

        await mutate()
        setUpdatingBasket(false)
      } catch (err) {
        alert('Failed to remove ticket. Please try again.')
        setUpdatingBasket(false)
      }
    }
  }

  const handleRemoveAllTickets = async (ticketIds, instanceId) => {
    if (
      window.confirm(
        'Are you sure you want to remove all these tickets from your basket?'
      )
    ) {
      try {
        setRemovingTickets(prev => {
          const updated = { ...prev }
          ticketIds.forEach(id => {
            updated[id] = true
          })
          return updated
        })

        // Get meal deal instances from any ticket's localInstanceData
        let mealDealInstances = []
        const instanceTicket = basketData?.tickets?.find(
          t => t.instance?.id === instanceId && t.localInstanceData
        )
        if (instanceTicket?.localInstanceData?.meal_deal_instances) {
          mealDealInstances =
            instanceTicket.localInstanceData.meal_deal_instances
        }

        // Use the common sync function to remove tickets and associated meal deals
        await syncMealDealsWithTickets({
          action: 'remove',
          ticketsToProcess: ticketIds,
          instanceData: null,
          mealDealInfo: {
            meal_deals: mealDealInstances
          },
          currentBasket: basketData
        })

        await mutate()
      } catch (err) {
        alert('Failed to remove tickets. Please try again.')
      } finally {
        setRemovingTickets(prev => {
          const updated = { ...prev }
          ticketIds.forEach(id => {
            delete updated[id]
          })
          return updated
        })
      }
    }
  }

  const handleRemoveSeat = async (ticketId, instanceId) => {
    if (
      window.confirm(
        'Are you sure you want to remove this seat from your basket?'
      )
    ) {
      try {
        setRemovingTickets(prev => ({ ...prev, [ticketId]: true }))

        // Get meal deal instances from any ticket's localInstanceData
        let mealDealInstances = []
        const instanceTicket = basketData?.tickets?.find(
          t => t.instance?.id === instanceId && t.localInstanceData
        )
        if (instanceTicket?.localInstanceData?.meal_deal_instances) {
          mealDealInstances =
            instanceTicket.localInstanceData.meal_deal_instances
        }

        // Use the common sync function
        await syncMealDealsWithTickets({
          action: 'remove',
          ticketsToProcess: [ticketId],
          instanceData: null,
          mealDealInfo: {
            meal_deals: mealDealInstances
          },
          currentBasket: basketData
        })

        await mutate()
      } catch (err) {
        alert('Failed to remove ticket. Please try again.')
      } finally {
        setRemovingTickets(prev => ({ ...prev, [ticketId]: false }))
      }
    }
  }

  const handleRemoveGiftVoucher = async voucherId => {
    if (
      window.confirm(
        'Are you sure you want to remove this gift voucher from your basket?'
      )
    ) {
      try {
        setRemovingGiftVouchers(prev => ({ ...prev, [voucherId]: true }))
        await removeGiftVoucherFromBasket(voucherId)
        await mutate()
      } catch (err) {
        alert('Failed to remove gift voucher. Please try again.')
      } finally {
        setRemovingGiftVouchers(prev => ({ ...prev, [voucherId]: false }))
      }
    }
  }

  const handleUpdateGiftVoucher = async (voucherId, updates) => {
    try {
      setUpdatingBasket(true)
      await updateGiftVoucherInBasket(voucherId, updates)
      await mutate()
      setEditingGiftVoucher(null)
    } catch (err) {
      alert('Failed to update gift voucher. Please try again.')
    } finally {
      setUpdatingBasket(false)
    }
  }

  const handleRemoveMembership = async subscriptionId => {
    if (
      window.confirm(
        'Are you sure you want to remove this membership from your basket?'
      )
    ) {
      try {
        setRemovingMemberships(prev => ({ ...prev, [subscriptionId]: true }))
        await removeMembershipFromBasket(subscriptionId)
        await mutate()
      } catch (err) {
        alert('Failed to remove membership. Please try again.')
      } finally {
        setRemovingMemberships(prev => ({ ...prev, [subscriptionId]: false }))
      }
    }
  }

  const handleUpdateMembership = async (subscriptionId, updates) => {
    try {
      setUpdatingBasket(true)
      await updateMembershipInBasket(subscriptionId, updates)
      await mutate()
      setEditingMembership(null)
    } catch (err) {
      alert('Failed to update membership. Please try again.')
    } finally {
      setUpdatingBasket(false)
    }
  }

  const groupItemsByEvent = () => {
    const items = basketData?.tickets || []
    if (!items || items.length === 0) return {}

    const grouped = {}
    const mealDealsByInstanceId = {}

    // First, separate meal deals and regular tickets
    items.forEach(item => {
      if (item.event && item.instance) {
        if (item.isMealDeal) {
          // Store meal deals by their instance ID
          const instanceId = item.instance.id
          if (!mealDealsByInstanceId[instanceId]) {
            mealDealsByInstanceId[instanceId] = []
          }
          mealDealsByInstanceId[instanceId].push(item)
        } else {
          // Regular event - use local event ID for grouping
          const localEventId = item.localEventData?.id || item.event.id
          const eventId = localEventId

          if (!grouped[eventId]) {
            grouped[eventId] = {
              event: item.event,
              localEvent: item.localEventData || {},
              instances: {}
            }
          }

          const instanceId = item.instance.id
          if (!grouped[eventId].instances[instanceId]) {
            grouped[eventId].instances[instanceId] = {
              instance: item.instance,
              localInstanceData: item.localInstanceData,
              items: [],
              mealDeals: []
            }
          }

          grouped[eventId].instances[instanceId].items.push(item)
        }
      }
    })

    // Second pass: Match meal deals to their parent instances
    Object.entries(grouped).forEach(([eventId, eventData]) => {
      Object.entries(eventData.instances).forEach(
        ([instanceId, instanceData]) => {
          // Check if this instance has meal deals available (from backend)
          const availableMealDeals =
            instanceData.localInstanceData?.meal_deal_instances || []

          // For each available meal deal instance, check if we have it in the basket
          availableMealDeals.forEach(mealDealInfo => {
            const mealDealInstanceId = mealDealInfo.spektrix_v3_id
            if (mealDealsByInstanceId[mealDealInstanceId]) {
              // Add these meal deals to this instance
              instanceData.mealDeals.push(
                ...mealDealsByInstanceId[mealDealInstanceId]
              )
              // Remove from the pool so they don't get added twice
              delete mealDealsByInstanceId[mealDealInstanceId]
            }
          })
        }
      )
    })

    // Any remaining meal deals that couldn't be matched go into orphaned section
    if (Object.keys(mealDealsByInstanceId).length > 0) {
      const orphanedInstances = {}

      Object.entries(mealDealsByInstanceId).forEach(
        ([instanceId, mealDeals]) => {
          orphanedInstances[instanceId] = {
            instance: mealDeals[0].instance,
            localInstanceData: mealDeals[0].localInstanceData,
            items: mealDeals
          }
        }
      )

      grouped['orphaned_meal_deals'] = {
        event: { id: 'orphaned_meal_deals', name: 'Pre-show Dinner Offers' },
        instances: orphanedInstances
      }
    }

    return grouped
  }

  const getOffersForInstance = instanceItems => {
    // Get offers from the actual basket items
    const offersInBasket = instanceItems
      .filter(item => item.offer)
      .map(item => item.offer)

    // Remove duplicates based on offer ID
    const uniqueOffers = offersInBasket.filter(
      (offer, index, self) =>
        index === self.findIndex(o => o && offer && o.id === offer.id)
    )

    return uniqueOffers
  }

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-16'>
        <ArrowPathIcon className='w-8 h-8 text-gray-500 dark:text-gray-400 animate-spin mr-2' />
        <span className='text-gray-700 dark:text-gray-300'>
          Loading basket...
        </span>
      </div>
    )
  }

  if (error) {
    return (
      <div className='bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4'>
        <h3 className='text-lg font-semibold text-red-700 dark:text-red-300 mb-2'>
          Failed to load basket
        </h3>
        <p className='text-red-600 dark:text-red-400 mb-3'>
          {error.message || 'An unexpected error occurred'}
        </p>
        {error.message?.includes('401') && (
          <p className='text-sm text-red-600 dark:text-red-400 mb-3'>
            Your session may have expired. Please refresh the page and try
            again.
          </p>
        )}
        <button
          onClick={() => mutate()}
          className='mt-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors'
        >
          Try again
        </button>
      </div>
    )
  }

  // Use basketData if available (basket has items), otherwise use spektrixBasket
  const effectiveBasket = basketData || spektrixBasket
  const items = effectiveBasket?.tickets || []
  const giftVouchers = effectiveBasket?.giftVouchers || []
  const subscriptions = effectiveBasket?.membershipSubscriptions || []

  if (!hasItems) {
    return (
      <div className='text-center py-16'>
        <ShoppingBagIcon className='w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4' />
        <h2 className='text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2'>
          Your basket is empty
        </h2>
        <p className='text-gray-600 dark:text-gray-400 mb-6'>
          Browse our events and add tickets to your basket
        </p>
        <a
          href='/whatson'
          className='inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors'
        >
          <ArrowLeftIcon className='w-4 h-4 mr-2' />
          Browse Events
        </a>
      </div>
    )
  }

  const groupedItems = groupItemsByEvent()

  // Collect all unique related events
  const collectRelatedEvents = () => {
    const allRelated = new Map()

    // First, collect all event IDs that are already in the basket
    const eventsInBasket = new Set()
    Object.entries(groupedItems).forEach(([eventId, eventData]) => {
      if (eventId !== 'orphaned_meal_deals') {
        // Add both the local event ID and the spektrix event ID to the set
        const event =
          eventDetails[eventId] || eventData.localEvent || eventData.event
        eventsInBasket.add(eventId)
        if (event?.id) eventsInBasket.add(event.id)
        if (event?.spektrix_id) eventsInBasket.add(event.spektrix_id)
        if (event?.spektrix_v3_id) eventsInBasket.add(event.spektrix_v3_id)
      }
    })

    Object.entries(groupedItems).forEach(([eventId, eventData]) => {
      if (eventId !== 'orphaned_meal_deals') {
        const event =
          eventDetails[eventId] || eventData.localEvent || eventData.event
        const eventRelated =
          relatedEvents[eventId] || relatedEvents[String(eventId)] || []

        eventRelated.forEach(related => {
          // Only add if not already in the related map AND not in the basket
          if (
            !allRelated.has(related.id) &&
            !eventsInBasket.has(related.id) &&
            !eventsInBasket.has(String(related.id))
          ) {
            allRelated.set(related.id, related)
          }
        })
      }
    })

    // Convert to array and sort by next instance date
    return Array.from(allRelated.values()).sort((a, b) => {
      // Parse the next instance date if available
      const getNextDate = event => {
        if (event.next_instance_date) {
          return new Date(event.next_instance_date)
        }
        // Fallback to parsing from dates HTML if needed
        if (event.dates) {
          const match = event.dates.match(/(\d{1,2}\s+\w+\s+\d{4})/)
          if (match) {
            return new Date(match[1])
          }
        }
        return new Date() // Default to current date if no date found
      }

      return getNextDate(a) - getNextDate(b)
    })
  }

  const uniqueRelatedEvents = collectRelatedEvents()

  return (
    <div className='grid grid-cols-1 lg:grid-cols-3 gap-8'>
      <div className='lg:col-span-2'>
        <div className='space-y-6'>
          {/* Offers Section */}
          {basketData?.offers && basketData.offers.length > 0 && (
            <div className='bg-amber-50 dark:bg-amber-900/20 rounded-lg shadow-sm border border-amber-200 dark:border-amber-800 p-3 sm:p-4'>
              <h3 className='text-sm font-semibold mb-2 text-gray-900 dark:text-white'>
                Available Offers
              </h3>
              <div className='space-y-1'>
                {basketData.offers.map((offer, index) => (
                  <div key={offer.id || index} className='flex items-start'>
                    <svg
                      className='w-4 h-4 text-amber-600 dark:text-amber-400 mr-2 mt-0.5 flex-shrink-0'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path d='M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z' />
                    </svg>
                    <div className='flex-1'>
                      {offer.description && (
                        <div
                          className='text-sm text-gray-700 dark:text-gray-300 prose prose-sm max-w-none'
                          dangerouslySetInnerHTML={{
                            __html: offer.description
                          }}
                        />
                      )}
                      {offer.requiresPromoCode && (
                        <span className='text-xs text-amber-700 dark:text-amber-300 font-medium ml-1'>
                          (Promo code required)
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {Object.entries(groupedItems).map(([eventId, eventData]) => {
            const event =
              eventDetails[eventId] || eventData.localEvent || eventData.event
            const isOrphaned = eventId === 'orphaned_meal_deals'

            return (
              <div
                key={eventId}
                className='bg-gray-50 dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700'
              >
                <div className='p-4 sm:p-6'>
                  {/* Event Header */}
                  {!isOrphaned && (
                    <div className='flex items-start space-x-4 mb-4'>
                      {(event.square_image_url || event.image_url) && (
                        <img
                          src={event.square_image_url || event.image_url}
                          alt={event.name}
                          className='w-24 h-24 object-cover rounded-lg'
                        />
                      )}
                      <div className='flex-1'>
                        <h3 className='text-xl font-bold font-serif mb-1'>
                          {event.name ? (
                            <a
                              href={`/events/${event.slug}`}
                              className='text-gray-900 dark:text-white hover:underline'
                            >
                              {event.name}
                            </a>
                          ) : (
                            <span className='text-gray-500 dark:text-gray-400'>
                              Loading event details...
                            </span>
                          )}
                        </h3>
                        {event.duration && event.duration !== 0 && (
                          <p className='text-sm text-gray-600 dark:text-gray-400'>
                            Duration: {event.duration} minutes
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Handle orphaned meal deals separately */}
                  {isOrphaned ? (
                    <>
                      <h3 className='text-xl font-bold font-serif mb-4 text-gray-900 dark:text-white'>
                        Pre-show Dinner Offers
                      </h3>
                      {Object.entries(eventData.instances).map(
                        ([instanceId, instanceData]) => {
                          if (
                            !instanceData.items ||
                            instanceData.items.length === 0
                          )
                            return null

                          const mealDealInstance =
                            instanceData.localInstanceData ||
                            instanceData.instance

                          return (
                            <div key={instanceId} className='mb-4'>
                              <div className='flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800'>
                                <div className='flex-1'>
                                  <p className='font-medium text-gray-700 dark:text-gray-300 flex items-center'>
                                    <svg
                                      className='w-4 h-4 mr-2 text-amber-600 dark:text-amber-400 flex-shrink-0'
                                      fill='currentColor'
                                      viewBox='0 0 24 24'
                                    >
                                      <circle
                                        cx='12'
                                        cy='12'
                                        r='9'
                                        fill='none'
                                        stroke='currentColor'
                                        strokeWidth='2'
                                      />
                                      <circle
                                        cx='12'
                                        cy='12'
                                        r='5'
                                        fill='none'
                                        stroke='currentColor'
                                        strokeWidth='1.5'
                                      />
                                    </svg>
                                    Pre-show Dinner Offer
                                    <span className='ml-2 text-sm text-gray-600 dark:text-gray-400'>
                                      ({instanceData.items.length}{' '}
                                      {instanceData.items.length === 1
                                        ? 'meal'
                                        : 'meals'}
                                      )
                                    </span>
                                  </p>
                                  <p className='text-sm text-gray-600 dark:text-gray-400 mt-1'>
                                    {mealDealInstance.start
                                      ? format(
                                          parseISO(mealDealInstance.start),
                                          standardWithTime
                                        )
                                      : 'Date & Time TBC'}
                                    {instanceData.items[0].localInstanceData
                                      ?.venue?.name && (
                                      <span className='ml-2'>
                                        •{' '}
                                        {
                                          instanceData.items[0]
                                            .localInstanceData.venue.name
                                        }
                                      </span>
                                    )}
                                  </p>
                                  <p className='text-sm text-gray-600 dark:text-gray-400'>
                                    Total: £
                                    {instanceData.items
                                      .reduce(
                                        (sum, meal) => sum + (meal.price || 0),
                                        0
                                      )
                                      .toFixed(2)}
                                  </p>
                                </div>

                                <button
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        `Are you sure you want to remove all ${
                                          instanceData.items.length
                                        } meal deal${
                                          instanceData.items.length === 1
                                            ? ''
                                            : 's'
                                        } from your basket?`
                                      )
                                    ) {
                                      handleRemoveAllTickets(
                                        instanceData.items.map(m => m.id),
                                        instanceId
                                      )
                                    }
                                  }}
                                  className='p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400'
                                  disabled={instanceData.items.some(
                                    m => removingTickets[m.id]
                                  )}
                                  title='Remove all meal deals'
                                >
                                  {instanceData.items.some(
                                    m => removingTickets[m.id]
                                  ) ? (
                                    <ArrowPathIcon className='w-4 h-4 animate-spin' />
                                  ) : (
                                    <TrashIcon className='w-4 h-4' />
                                  )}
                                </button>
                              </div>
                            </div>
                          )
                        }
                      )}
                    </>
                  ) : (
                    /* Regular instances */
                    Object.entries(eventData.instances).map(
                      ([instanceId, instanceData]) => {
                        const instance =
                          instanceDetails[instanceId] || instanceData.instance

                        return (
                          <div
                            key={instanceId}
                            className='border-t border-gray-200 dark:border-gray-700 pt-4 mt-4'
                          >
                            <div className='mb-3'>
                              <p className='font-semibold text-gray-700 dark:text-gray-300'>
                                {instance.start
                                  ? format(
                                      parseISO(instance.start),
                                      standardWithTime
                                    )
                                  : 'Performance'}
                              </p>
                              {instance.venue && (
                                <p className='text-sm text-gray-600 dark:text-gray-400'>
                                  {instance.venue.name}
                                </p>
                              )}
                            </div>

                            {/* Items for this instance */}
                            <div className='space-y-2'>
                              {(() => {
                                // Separate reserved and unreserved tickets
                                const reservedTickets =
                                  instanceData.items.filter(
                                    item => item.seatName
                                  )
                                const unreservedTickets =
                                  instanceData.items.filter(
                                    item => !item.seatName
                                  )

                                // Group unreserved tickets by type
                                const unreservedByType =
                                  unreservedTickets.reduce((acc, item) => {
                                    const key = `${item.planName || 'Ticket'}_${
                                      item.price || 0
                                    }_${item.band || 'standard'}`
                                    if (!acc[key]) {
                                      acc[key] = {
                                        planName: item.planName || 'Ticket',
                                        price: item.total || 0,
                                        tickets: [],
                                        // Store plan data from the first ticket of this type
                                        plan: item.plan,
                                        band: item.band
                                      }
                                    }
                                    acc[key].tickets.push(item)
                                    return acc
                                  }, {})

                                return (
                                  <>
                                    {/* Reserved tickets - show individually */}
                                    {reservedTickets.map(item => (
                                      <div
                                        key={item.id}
                                        className='flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg'
                                      >
                                        <div className='flex-1'>
                                          <p className='font-medium text-gray-700 dark:text-gray-300 flex items-center'>
                                            <TicketIcon className='w-4 h-4 mr-2 -rotate-12 text-blue-600 dark:text-blue-400 flex-shrink-0' />
                                            {item.planName || 'Ticket'}
                                            <span className='ml-2 text-sm text-gray-600 dark:text-gray-400'>
                                              Seat {item.seatName}
                                            </span>
                                          </p>
                                          <p className='text-sm text-gray-600 dark:text-gray-400'>
                                            £{(item.price || 0).toFixed(2)}
                                          </p>
                                        </div>

                                        <div className='flex items-center space-x-2'>
                                          {event.slug && instance.start && (
                                            <a
                                              href={`/events/${
                                                event.slug
                                              }/${format(
                                                parseISO(instance.start),
                                                'yyyy-MM-dd'
                                              )}/${format(
                                                parseISO(instance.start),
                                                'HH:mm'
                                              )}/add_to_basket`}
                                              className='text-sm text-blue-600 dark:text-blue-400 hover:underline'
                                            >
                                              Change seat
                                            </a>
                                          )}
                                          <button
                                            onClick={() =>
                                              handleRemoveSeat(
                                                item.id,
                                                instanceId
                                              )
                                            }
                                            className='p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400'
                                            disabled={removingTickets[item.id]}
                                            title='Remove seat'
                                          >
                                            {removingTickets[item.id] ? (
                                              <ArrowPathIcon className='w-4 h-4 animate-spin' />
                                            ) : (
                                              <TrashIcon className='w-4 h-4' />
                                            )}
                                          </button>
                                        </div>
                                      </div>
                                    ))}

                                    {/* Unreserved tickets - group by type */}
                                    {Object.entries(unreservedByType).map(
                                      ([key, group]) => (
                                        <div
                                          key={key}
                                          className='flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg'
                                        >
                                          <div className='flex-1'>
                                            <p className='font-medium text-gray-700 dark:text-gray-300 flex items-center'>
                                              <TicketIcon className='w-4 h-4 mr-2 -rotate-12 text-blue-600 dark:text-blue-400 flex-shrink-0' />
                                              {group.planName}
                                              <span className='ml-2 text-sm text-gray-600 dark:text-gray-400'>
                                                (Unreserved)
                                              </span>
                                            </p>
                                            <p className='text-sm text-gray-600 dark:text-gray-400'>
                                              £{group.price.toFixed(2)} each
                                            </p>
                                          </div>

                                          {/* Mobile layout - quantity controls below ticket info */}
                                          <div className='flex items-center justify-between mt-3 sm:mt-0 sm:ml-3'>
                                            {/* Quantity controls */}
                                            <div className='flex items-center space-x-2'>
                                              <button
                                                onClick={() =>
                                                  handleQuantityChange(
                                                    {
                                                      ...group,
                                                      event,
                                                      instance,
                                                      planId: group.plan
                                                    },
                                                    -1
                                                  )
                                                }
                                                disabled={
                                                  updatingBasket ||
                                                  group.tickets.length === 0 ||
                                                  removingTickets[
                                                    group.tickets[
                                                      group.tickets.length - 1
                                                    ]?.id
                                                  ]
                                                }
                                                className='disabled:opacity-50 group'
                                                title='Remove one ticket'
                                              >
                                                {removingTickets[
                                                  group.tickets[
                                                    group.tickets.length - 1
                                                  ]?.id
                                                ] ? (
                                                  <ArrowPathIcon className='w-6 h-6 sm:w-8 sm:h-8 animate-spin text-gray-400' />
                                                ) : (
                                                  <MinusCircleIcon className='w-6 h-6 sm:w-8 sm:h-8 text-blue-600 dark:text-blue-400 group-enabled:hover:bg-blue-600 dark:group-enabled:hover:bg-blue-500 rounded-full group-enabled:hover:text-white' />
                                                )}
                                              </button>

                                              <span className='px-2 sm:px-3 py-1 min-w-[2.5rem] sm:min-w-[3rem] text-center font-medium text-gray-700 dark:text-gray-300'>
                                                {group.tickets.length}
                                              </span>

                                              <button
                                                onClick={() =>
                                                  handleQuantityChange(
                                                    {
                                                      ...group,
                                                      event,
                                                      instance,
                                                      planId: group.plan
                                                    },
                                                    1
                                                  )
                                                }
                                                disabled={updatingBasket}
                                                className='disabled:opacity-50 group'
                                                title='Add one ticket'
                                              >
                                                {updatingBasket ? (
                                                  <ArrowPathIcon className='w-6 h-6 sm:w-8 sm:h-8 animate-spin text-gray-400' />
                                                ) : (
                                                  <PlusCircleIcon className='w-6 h-6 sm:w-8 sm:h-8 text-blue-600 dark:text-blue-400 group-enabled:hover:bg-blue-600 dark:group-enabled:hover:bg-blue-500 rounded-full group-enabled:hover:text-white' />
                                                )}
                                              </button>
                                            </div>

                                            {/* Edit and remove buttons on same line as quantity on mobile */}
                                            <div className='flex items-center space-x-2 sm:space-x-3 ml-3 sm:ml-6'>
                                              {/* Edit link */}
                                              {event.slug && instance.start && (
                                                <a
                                                  href={`/events/${
                                                    event.slug
                                                  }/${format(
                                                    parseISO(instance.start),
                                                    'yyyy-MM-dd'
                                                  )}/${format(
                                                    parseISO(instance.start),
                                                    'HH:mm'
                                                  )}/add_to_basket`}
                                                  className='text-sm text-blue-600 dark:text-blue-400 hover:underline'
                                                >
                                                  Edit
                                                </a>
                                              )}

                                              {/* Remove all button */}
                                              <button
                                                onClick={() =>
                                                  handleRemoveAllTickets(
                                                    group.tickets.map(t => t.id),
                                                    instanceId
                                                  )
                                                }
                                                className='p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400'
                                                disabled={group.tickets.some(
                                                  t => removingTickets[t.id]
                                                )}
                                                title='Remove all tickets'
                                              >
                                                {group.tickets.some(
                                                  t => removingTickets[t.id]
                                                ) ? (
                                                  <ArrowPathIcon className='w-4 h-4 animate-spin' />
                                                ) : (
                                                  <TrashIcon className='w-4 h-4' />
                                                )}
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      )
                                    )}
                                  </>
                                )
                              })()}
                            </div>

                            {/* Meal Deals for this instance - Show as single entry */}
                            {instanceData.mealDeals &&
                              instanceData.mealDeals.length > 0 && (
                                <div className='mt-4'>
                                  <div className='flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800'>
                                    <div className='flex-1'>
                                      <p className='font-medium text-gray-700 dark:text-gray-300 flex items-center'>
                                        <svg
                                          className='w-4 h-4 mr-2 text-amber-600 dark:text-amber-400 flex-shrink-0'
                                          fill='currentColor'
                                          viewBox='0 0 24 24'
                                        >
                                          <circle
                                            cx='12'
                                            cy='12'
                                            r='9'
                                            fill='none'
                                            stroke='currentColor'
                                            strokeWidth='2'
                                          />
                                          <circle
                                            cx='12'
                                            cy='12'
                                            r='5'
                                            fill='none'
                                            stroke='currentColor'
                                            strokeWidth='1.5'
                                          />
                                        </svg>
                                        Pre-show Dinner Offer
                                        <span className='ml-2 text-sm text-gray-600 dark:text-gray-400'>
                                          ({instanceData.mealDeals.length}{' '}
                                          {instanceData.mealDeals.length === 1
                                            ? 'meal'
                                            : 'meals'}
                                          )
                                        </span>
                                      </p>
                                      <p className='text-sm text-gray-600 dark:text-gray-400 mt-1'>
                                        {/* Show first meal deal's venue/time as they should all be the same */}
                                        {instanceData.mealDeals[0]
                                          .localInstanceData?.venue?.name && (
                                          <span>
                                            {
                                              instanceData.mealDeals[0]
                                                .localInstanceData.venue.name
                                            }
                                          </span>
                                        )}
                                        {instanceData.mealDeals[0]
                                          .localInstanceData?.start && (
                                          <span className='ml-2'>
                                            •{' '}
                                            {format(
                                              parseISO(
                                                instanceData.mealDeals[0]
                                                  .localInstanceData.start
                                              ),
                                              'EEE d MMM, HH:mm'
                                            )}
                                          </span>
                                        )}
                                      </p>
                                      <p className='text-sm text-gray-600 dark:text-gray-400'>
                                        Total: £
                                        {instanceData.mealDeals
                                          .reduce(
                                            (sum, meal) =>
                                              sum + (meal.price || 0),
                                            0
                                          )
                                          .toFixed(2)}
                                      </p>
                                    </div>

                                    <button
                                      onClick={() => {
                                        if (
                                          window.confirm(
                                            `Are you sure you want to remove all ${
                                              instanceData.mealDeals.length
                                            } meal deal${
                                              instanceData.mealDeals.length ===
                                              1
                                                ? ''
                                                : 's'
                                            } from your basket?`
                                          )
                                        ) {
                                          handleRemoveAllTickets(
                                            instanceData.mealDeals.map(
                                              m => m.id
                                            ),
                                            instanceId
                                          )
                                        }
                                      }}
                                      className='p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400'
                                      disabled={instanceData.mealDeals.some(
                                        m => removingTickets[m.id]
                                      )}
                                      title='Remove all meal deals'
                                    >
                                      {instanceData.mealDeals.some(
                                        m => removingTickets[m.id]
                                      ) ? (
                                        <ArrowPathIcon className='w-4 h-4 animate-spin' />
                                      ) : (
                                        <TrashIcon className='w-4 h-4' />
                                      )}
                                    </button>
                                  </div>
                                </div>
                              )}
                          </div>
                        )
                      }
                    )
                  )}
                </div>
              </div>
            )
          })}

          {/* Gift Vouchers */}
          {giftVouchers.length > 0 && (
            <div
              className={`bg-gray-50 dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 ${
                Object.keys(groupedItems).length > 0 ? 'mt-6' : ''
              }`}
            >
              <div className='p-4 sm:p-6'>
                <h3 className='text-xl font-bold font-serif mb-4 text-gray-900 dark:text-white flex items-center'>
                  <GiftIcon className='w-6 h-6 mr-2 text-blue-600 dark:text-blue-400' />
                  Gift Vouchers
                </h3>
                <div className='space-y-4'>
                  {giftVouchers.map(voucher => (
                    <div
                      key={voucher.id}
                      className='border border-gray-200 dark:border-gray-700 rounded-lg p-4'
                    >
                      {editingGiftVoucher === voucher.id ? (
                        // Edit form
                        <GiftVoucherEditForm
                          voucher={voucher}
                          onSave={updates =>
                            handleUpdateGiftVoucher(voucher.id, updates)
                          }
                          onCancel={() => setEditingGiftVoucher(null)}
                          disabled={updatingBasket}
                        />
                      ) : (
                        // Display view
                        <div className='flex items-start justify-between'>
                          <div className='flex-1'>
                            <div className='flex items-center mb-2'>
                              <span className='text-lg font-semibold text-gray-900 dark:text-white'>
                                £{voucher.amount.toFixed(2)}
                              </span>
                            </div>
                            <div className='space-y-1 text-sm text-gray-600 dark:text-gray-400'>
                              <p>
                                <span className='font-medium'>To:</span>{' '}
                                {voucher.toName}
                              </p>
                              <p>
                                <span className='font-medium'>From:</span>{' '}
                                {voucher.fromName}
                              </p>
                              <p>
                                <span className='font-medium'>Delivery:</span>{' '}
                                {voucher.deliveryType === 'CustomerEmail'
                                  ? 'To purchaser'
                                  : 'To recipient'}
                                {voucher.deliveryEmailAddress && (
                                  <span className='ml-1'>
                                    ({voucher.deliveryEmailAddress})
                                  </span>
                                )}
                              </p>
                              {voucher.sendDate && (
                                <p>
                                  <span className='font-medium'>
                                    Send Date:
                                  </span>{' '}
                                  {new Date(
                                    voucher.sendDate
                                  ).toLocaleDateString()}
                                </p>
                              )}
                              {voucher.message && (
                                <p className='mt-2 italic'>
                                  "{voucher.message}"
                                </p>
                              )}
                            </div>
                          </div>
                          <div className='flex items-center space-x-2 ml-4'>
                            <button
                              onClick={() => setEditingGiftVoucher(voucher.id)}
                              className='p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors'
                              title='Edit gift voucher'
                            >
                              <PencilIcon className='w-4 h-4' />
                            </button>
                            <button
                              onClick={() =>
                                handleRemoveGiftVoucher(voucher.id)
                              }
                              className='p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors'
                              disabled={removingGiftVouchers[voucher.id]}
                              title='Remove gift voucher'
                            >
                              {removingGiftVouchers[voucher.id] ? (
                                <ArrowPathIcon className='w-4 h-4 animate-spin' />
                              ) : (
                                <TrashIcon className='w-4 h-4' />
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Memberships */}
          {subscriptions.length > 0 && (
            <div
              className={`bg-gray-50 dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 ${
                Object.keys(groupedItems).length > 0 || giftVouchers.length > 0
                  ? 'mt-6'
                  : ''
              }`}
            >
              <div className='p-4 sm:p-6'>
                <h3 className='text-xl font-bold font-serif mb-4 text-gray-900 dark:text-white flex items-center'>
                  <UserGroupIcon className='w-6 h-6 mr-2 text-blue-600 dark:text-blue-400' />
                  Memberships
                </h3>
                <div className='space-y-4'>
                  {subscriptions.map(subscription => (
                    <div
                      key={subscription.id}
                      className='border border-gray-200 dark:border-gray-700 rounded-lg p-4'
                    >
                      {editingMembership === subscription.id ? (
                        // Edit form
                        <div className='space-y-4'>
                          <div>
                            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                              Auto-renewal
                            </label>
                            <div className='flex items-center space-x-4'>
                              <label className='flex items-center'>
                                <input
                                  type='radio'
                                  name={`autoRenew-${subscription.id}`}
                                  value='true'
                                  defaultChecked={
                                    subscription.autoRenew === true
                                  }
                                  onChange={e => {
                                    subscription._newAutoRenew =
                                      e.target.value === 'true'
                                  }}
                                  className='mr-2 text-blue-600 focus:ring-blue-500'
                                />
                                <span className='text-sm text-gray-700 dark:text-gray-300'>
                                  Enabled
                                </span>
                              </label>
                              <label className='flex items-center'>
                                <input
                                  type='radio'
                                  name={`autoRenew-${subscription.id}`}
                                  value='false'
                                  defaultChecked={
                                    subscription.autoRenew === false
                                  }
                                  onChange={e => {
                                    subscription._newAutoRenew =
                                      e.target.value === 'true'
                                  }}
                                  className='mr-2 text-blue-600 focus:ring-blue-500'
                                />
                                <span className='text-sm text-gray-700 dark:text-gray-300'>
                                  Disabled
                                </span>
                              </label>
                            </div>
                          </div>
                          <div className='flex justify-end space-x-2'>
                            <button
                              onClick={() => setEditingMembership(null)}
                              className='px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() =>
                                handleUpdateMembership(subscription.id, {
                                  autoRenew:
                                    subscription._newAutoRenew !== undefined
                                      ? subscription._newAutoRenew
                                      : subscription.autoRenew
                                })
                              }
                              disabled={updatingBasket}
                              className='px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50'
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      ) : (
                        // Display view
                        <div className='flex items-start justify-between'>
                          <div className='flex-1'>
                            <div className='flex items-center mb-2'>
                              <span className='text-lg font-semibold text-gray-900 dark:text-white'>
                                {subscription.membership?.name || 'Membership'}
                              </span>
                              <span className='ml-2 text-lg text-gray-700 dark:text-gray-300'>
                                £{subscription.price?.toFixed(2) || '0.00'}
                              </span>
                            </div>
                            <div className='space-y-1 text-sm text-gray-600 dark:text-gray-400'>
                              {subscription.startDate && (
                                <p>
                                  <span className='font-medium'>Starts:</span>{' '}
                                  {new Date(
                                    subscription.startDate
                                  ).toLocaleDateString()}
                                </p>
                              )}
                              {subscription.expiryDate && (
                                <p>
                                  <span className='font-medium'>Expires:</span>{' '}
                                  {new Date(
                                    subscription.expiryDate
                                  ).toLocaleDateString()}
                                </p>
                              )}
                              <p>
                                <span className='font-medium'>
                                  Auto-renewal:
                                </span>{' '}
                                <span
                                  className={
                                    subscription.autoRenew
                                      ? 'text-green-600 dark:text-green-400'
                                      : 'text-gray-500 dark:text-gray-400'
                                  }
                                >
                                  {subscription.autoRenew
                                    ? 'Enabled'
                                    : 'Disabled'}
                                </span>
                              </p>
                              {subscription.membership?.htmlDescription && (
                                <div
                                  className='mt-2 prose prose-sm max-w-none'
                                  dangerouslySetInnerHTML={{
                                    __html:
                                      subscription.membership.htmlDescription
                                  }}
                                />
                              )}
                            </div>
                          </div>
                          <div className='flex items-center space-x-2 ml-4'>
                            <button
                              onClick={() =>
                                setEditingMembership(subscription.id)
                              }
                              className='p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors'
                              title='Edit membership'
                            >
                              <PencilIcon className='w-4 h-4' />
                            </button>
                            <button
                              onClick={() =>
                                handleRemoveMembership(subscription.id)
                              }
                              className='p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors'
                              disabled={removingMemberships[subscription.id]}
                              title='Remove membership'
                            >
                              {removingMemberships[subscription.id] ? (
                                <ArrowPathIcon className='w-4 h-4 animate-spin' />
                              ) : (
                                <TrashIcon className='w-4 h-4' />
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Consolidated Related Events */}
        {uniqueRelatedEvents.length > 0 && (
          <div className='bg-gray-50 dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 mt-6'>
            <h3 className='text-lg font-bold font-serif mb-4 text-gray-900 dark:text-white'>
              You may also be interested in
            </h3>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
              {uniqueRelatedEvents.map(related => (
                <a
                  key={related.id}
                  href={`/events/${related.slug || related.id}`}
                  className='group flex items-start space-x-3 p-3 bg-white dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-gray-700'
                >
                  {/* Square image */}
                  <div className='flex-shrink-0 w-16 h-16 bg-gray-200 dark:bg-gray-600 rounded-lg overflow-hidden'>
                    {related.square_image_url ? (
                      <img
                        src={related.square_image_url}
                        alt={related.name}
                        className='w-full h-full object-cover group-hover:scale-105 transition-transform duration-200'
                      />
                    ) : related.image_url ? (
                      <img
                        src={related.image_url}
                        alt={related.name}
                        className='w-full h-full object-cover group-hover:scale-105 transition-transform duration-200'
                      />
                    ) : (
                      <div className='w-full h-full flex items-center justify-center'>
                        <svg
                          className='w-8 h-8 text-gray-400 dark:text-gray-500'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'
                          />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className='flex-1 min-w-0'>
                    <p className='font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 line-clamp-2'>
                      {related.name}
                    </p>
                    {/* Date range and price */}
                    {related.dates && (
                      <p
                        className='mt-1 text-sm text-gray-600 dark:text-gray-400'
                        dangerouslySetInnerHTML={{
                          __html: related.dates
                        }}
                      />
                    )}
                    <p className='mt-1 text-sm text-blue-600 dark:text-blue-400 group-hover:underline'>
                      View dates & book →
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Summary Sidebar */}
      <div className='lg:col-span-1'>
        {/* Promo Code Section */}
        <PromoCodeSection 
          basketData={effectiveBasket}
          onUpdate={mutate}
        />
        
        <div className='bg-gray-50 dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 sticky top-4'>
          <h3 className='text-lg font-bold font-serif mb-4 text-gray-900 dark:text-white'>
            Order Summary
          </h3>

          <div className='space-y-2 mb-4'>
            <div className='flex justify-between text-sm'>
              <span className='text-gray-600 dark:text-gray-400'>Subtotal</span>
              <span className='font-medium text-gray-900 dark:text-white'>
                £
                {(
                  (effectiveBasket?.total || 0) -
                  (effectiveBasket?.transactionCommission?.amount || 0) +
                  (effectiveBasket?.discount ||
                    effectiveBasket?.totalDiscount ||
                    0)
                ).toFixed(2)}
              </span>
            </div>

            {effectiveBasket?.transactionCommission &&
              effectiveBasket.transactionCommission.amount > 0 && (
                <div className='flex justify-between text-sm'>
                  <span className='text-gray-600 dark:text-gray-400'>
                    Booking Fee
                  </span>
                  <span className='font-medium text-gray-900 dark:text-white'>
                    £{effectiveBasket.transactionCommission.amount.toFixed(2)}
                  </span>
                </div>
              )}

            {/* Show individual offer discounts */}
            {(() => {
              // Collect all unique offers with discounts from tickets
              const offerDiscounts = new Map()

              if (effectiveBasket?.tickets) {
                effectiveBasket.tickets.forEach(ticket => {
                  if (ticket.offer && ticket.discount > 0) {
                    const offerId = ticket.offer.id
                    if (offerDiscounts.has(offerId)) {
                      offerDiscounts.get(offerId).totalDiscount +=
                        ticket.discount
                      offerDiscounts.get(offerId).count += 1
                    } else {
                      offerDiscounts.set(offerId, {
                        name: ticket.offer.name,
                        description:
                          ticket.offer.htmlDescription ||
                          ticket.offer.description,
                        totalDiscount: ticket.discount,
                        count: 1
                      })
                    }
                  }
                })
              }

              return Array.from(offerDiscounts.entries()).map(
                ([offerId, offerData]) => (
                  <div
                    key={offerId}
                    className='flex justify-between text-sm text-green-600 dark:text-green-400'
                  >
                    <span className='flex items-start flex-1 mr-2'>
                      <svg
                        className='w-3 h-3 mr-1 mt-0.5 flex-shrink-0'
                        fill='currentColor'
                        viewBox='0 0 20 20'
                      >
                        <path d='M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z' />
                      </svg>
                      {offerData.description ? (
                        <span
                          dangerouslySetInnerHTML={{
                            __html: offerData.description
                          }}
                        />
                      ) : (
                        offerData.name
                      )}
                    </span>
                    <span className='flex-shrink-0'>
                      -£{offerData.totalDiscount.toFixed(2)}
                    </span>
                  </div>
                )
              )
            })()}

            {/* Show total discount if no individual offers */}
            {effectiveBasket?.totalDiscount > 0 &&
              (!effectiveBasket?.tickets ||
                !effectiveBasket.tickets.some(
                  t => t.offer && t.discount > 0
                )) && (
                <div className='flex justify-between text-sm text-green-600 dark:text-green-400'>
                  <span>Discount</span>
                  <span>-£{effectiveBasket.totalDiscount.toFixed(2)}</span>
                </div>
              )}
          </div>

          <div className='border-t border-gray-200 dark:border-gray-700 pt-4 mb-6'>
            <div className='flex justify-between'>
              <span className='text-lg font-semibold text-gray-900 dark:text-white'>
                Total
              </span>
              <span className='text-lg font-bold text-gray-900 dark:text-white'>
                £{(effectiveBasket?.total || 0).toFixed(2)}
              </span>
            </div>
          </div>

          <a
            href='/checkout'
            className='block w-full text-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium'
          >
            Proceed to Checkout
          </a>

          <a
            href='/whatson'
            className='block w-full text-center px-6 py-3 mt-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors'
          >
            Continue Shopping
          </a>
        </div>
      </div>
    </div>
  )
}

// Gift Voucher Edit Form Component
const GiftVoucherEditForm = ({ voucher, onSave, onCancel, disabled }) => {
  const [formData, setFormData] = useState({
    amount: voucher.amount,
    toName: voucher.toName,
    fromName: voucher.fromName,
    deliveryType: voucher.deliveryType || 'OtherEmail',
    deliveryEmailAddress: voucher.deliveryEmailAddress || '',
    sendDate: voucher.sendDate ? voucher.sendDate.split('T')[0] : '',
    message: voucher.message || ''
  })

  const handleSubmit = e => {
    e.preventDefault()
    // Clear email address if sending to customer
    const dataToSave = {
      ...formData,
      deliveryEmailAddress:
        formData.deliveryType === 'OtherEmail'
          ? formData.deliveryEmailAddress
          : undefined
    }
    onSave(dataToSave)
  }

  return (
    <form onSubmit={handleSubmit} className='space-y-4'>
      <div>
        <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
          Amount
        </label>
        <div className='relative'>
          <span className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400'>
            £
          </span>
          <input
            type='number'
            min='1'
            step='0.01'
            value={formData.amount}
            onChange={e =>
              setFormData({ ...formData, amount: parseFloat(e.target.value) })
            }
            className='w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
            required
            disabled={disabled}
          />
        </div>
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
            Recipient Name
          </label>
          <input
            type='text'
            value={formData.toName}
            onChange={e => setFormData({ ...formData, toName: e.target.value })}
            className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
            required
            disabled={disabled}
          />
        </div>
        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
            Your Name
          </label>
          <input
            type='text'
            value={formData.fromName}
            onChange={e =>
              setFormData({ ...formData, fromName: e.target.value })
            }
            className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
            required
            disabled={disabled}
          />
        </div>
      </div>

      <div className='space-y-4'>
        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
            Delivery Method
          </label>
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-2'>
            <label
              className={`relative flex items-center p-2.5 border-2 rounded-lg cursor-pointer transition-colors ${
                formData.deliveryType === 'OtherEmail'
                  ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-300 dark:border-gray-600'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input
                type='radio'
                name='editDeliveryType'
                value='OtherEmail'
                checked={formData.deliveryType === 'OtherEmail'}
                onChange={e =>
                  setFormData({ ...formData, deliveryType: e.target.value })
                }
                className='sr-only'
                disabled={disabled}
              />
              <div className='flex items-center'>
                <div
                  className={`w-4 h-4 rounded-full border-2 mr-2.5 flex items-center justify-center flex-shrink-0 ${
                    formData.deliveryType === 'OtherEmail'
                      ? 'border-blue-600'
                      : 'border-gray-400 dark:border-gray-500'
                  }`}
                >
                  {formData.deliveryType === 'OtherEmail' && (
                    <div className='w-2 h-2 rounded-full bg-blue-600' />
                  )}
                </div>
                <div>
                  <p className='font-medium text-gray-900 dark:text-white text-sm !my-0'>
                    To recipient
                  </p>
                  <p className='text-xs text-gray-600 dark:text-gray-400 mt-0.5 !mb-0'>
                    Send directly
                  </p>
                </div>
              </div>
            </label>

            <label
              className={`relative flex items-center p-2.5 border-2 rounded-lg cursor-pointer transition-colors ${
                formData.deliveryType === 'CustomerEmail'
                  ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-300 dark:border-gray-600'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input
                type='radio'
                name='editDeliveryType'
                value='CustomerEmail'
                checked={formData.deliveryType === 'CustomerEmail'}
                onChange={e =>
                  setFormData({ ...formData, deliveryType: e.target.value })
                }
                className='sr-only'
                disabled={disabled}
              />
              <div className='flex items-center'>
                <div
                  className={`w-4 h-4 rounded-full border-2 mr-2.5 flex items-center justify-center flex-shrink-0 ${
                    formData.deliveryType === 'CustomerEmail'
                      ? 'border-blue-600'
                      : 'border-gray-400 dark:border-gray-500'
                  }`}
                >
                  {formData.deliveryType === 'CustomerEmail' && (
                    <div className='w-2 h-2 rounded-full bg-blue-600' />
                  )}
                </div>
                <div>
                  <p className='font-medium text-gray-900 dark:text-white text-sm !my-0'>
                    To me
                  </p>
                  <p className='text-xs text-gray-600 dark:text-gray-400 mt-0.5 !mb-0'>
                    I'll forward it
                  </p>
                </div>
              </div>
            </label>
          </div>
        </div>

        {formData.deliveryType === 'OtherEmail' && (
          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
              Recipient Email
            </label>
            <input
              type='email'
              value={formData.deliveryEmailAddress}
              onChange={e =>
                setFormData({
                  ...formData,
                  deliveryEmailAddress: e.target.value
                })
              }
              className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
              placeholder='Recipient email address'
              required
              disabled={disabled}
            />
          </div>
        )}
        {formData.deliveryType === 'CustomerEmail' && (
          <div className='bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800'>
            <p className='text-sm text-blue-700 dark:text-blue-300 !m-0'>
              The voucher will be sent to your account email address
            </p>
          </div>
        )}

        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
            Send Date (Optional)
          </label>
          <input
            type='date'
            value={formData.sendDate}
            onChange={e =>
              setFormData({ ...formData, sendDate: e.target.value })
            }
            min={new Date().toISOString().split('T')[0]}
            className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
            disabled={disabled}
          />
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
            Leave blank to send immediately, or choose a future date
          </p>
        </div>
      </div>

      <div>
        <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
          Message (Optional)
        </label>
        <textarea
          rows={3}
          value={formData.message}
          onChange={e => setFormData({ ...formData, message: e.target.value })}
          className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
          disabled={disabled}
        />
      </div>

      <div className='flex justify-end space-x-3'>
        <button
          type='button'
          onClick={onCancel}
          className='px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg transition-colors'
          disabled={disabled}
        >
          Cancel
        </button>
        <button
          type='submit'
          className='px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors'
          disabled={disabled}
        >
          {disabled ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  )
}

export default BasketView
